import { MainLayout } from 'components/Layout';
import { useEffect } from 'react';
import JonahImg from 'assets/team/jonah.png';

export const Company = () => {
  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth', // change to 'auto' if you want instant
    });
  }, []);
  return (
    <MainLayout
      title="Company | Fundo"
      description="Fundo is building the infrastructure layer for modern, inclusive agriculture."
    >
      {/* HERO */}
      <section className="max-w-4xl">
        <p className="text-xs uppercase tracking-[0.35em] text-[#8b5e3c]">Company</p>

        <h1 className="mt-4 font-serif text-5xl leading-[0.95] tracking-[-0.04em] text-[#1b140f] sm:text-6xl">
          Building the operating system for agriculture.
        </h1>

        <p className="mt-6 text-lg leading-8 text-[#4f4034]">
          Fundo is redefining how agriculture works — transforming it from a fragmented, offline
          activity into a connected, data-driven, and participatory economy.
        </p>
      </section>

      {/* MISSION */}
      <section className="mt-24 grid gap-12 lg:grid-cols-2">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-[#8b5e3c]">Mission</p>

          <h2 className="mt-4 font-serif text-4xl text-[#1b140f]">Expand access to agriculture.</h2>

          <p className="mt-6 text-[#4f4034] leading-8">
            Agriculture should not be limited by geography, access to capital, or access to
            technology. Fundo enables anyone — from rural farmers to urban participants — to engage
            meaningfully in agricultural production and markets.
          </p>
        </div>

        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-[#8b5e3c]">Philosophy</p>

          <h2 className="mt-4 font-serif text-4xl text-[#1b140f]">
            Participation over limitation.
          </h2>

          <p className="mt-6 text-[#4f4034] leading-8">
            We believe agriculture should be open. Farmers should have better tools. Non-farmers
            should have entry points. Markets should be transparent. And financial systems should
            work with real data — not guesswork.
          </p>
        </div>
      </section>

      {/* WHAT WE'RE BUILDING */}
      <section className="mt-32">
        <div className="max-w-2xl">
          <p className="text-xs uppercase tracking-[0.3em] text-[#8b5e3c]">Platform</p>

          <h2 className="mt-4 font-serif text-4xl text-[#1b140f]">
            A unified agriculture ecosystem.
          </h2>

          <p className="mt-6 text-[#4f4034] leading-8">
            Fundo combines farm management, financial services, and market access into a single
            system. Farmers can track and optimize their operations, while others can participate
            through virtual farming, trading, and auctions.
          </p>
        </div>

        <div className="mt-12 grid gap-6 md:grid-cols-3">
          {[
            {
              title: 'Operate',
              desc: 'Manage real farms with structured data and AI insights.',
            },
            {
              title: 'Participate',
              desc: 'Engage in agriculture through virtual farming and shared production.',
            },
            {
              title: 'Transact',
              desc: 'Buy, sell, and trade through transparent digital markets.',
            },
          ].map((item, i) => (
            <div
              key={i}
              className="rounded-[1.75rem] border border-[#1b140f]/10 bg-white/60 p-6 backdrop-blur-md"
            >
              <h3 className="font-semibold text-[#1b140f]">{item.title}</h3>
              <p className="mt-3 text-sm text-[#4f4034] leading-7">{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* TEAM */}
      {/* TEAM */}
      <section className="mt-40">
        <div className="max-w-6xl">
          <p className="text-xs uppercase tracking-[0.3em] text-[#8b5e3c]">Team</p>

          <h2 className="mt-4 font-serif text-5xl leading-[1.05] tracking-[-0.03em] text-[#1b140f]">
            Built from real farming experience.
            <br /> Engineered for scale.
          </h2>
        </div>

        {/* GRID */}
        <div className="mt-16 grid gap-12 lg:grid-cols-2 items-center">
          {/* LEFT — FOUNDER */}
          <div className="relative">
            <div className="absolute -inset-4 bg-gradient-to-br from-[#8b5e3c]/10 to-transparent blur-2xl rounded-[2rem]" />

            <div className="relative rounded-[2rem] border border-[#1b140f]/10 bg-white/80 backdrop-blur-xl p-8">
              <div className="flex items-center gap-6">
                <img
                  src={JonahImg}
                  alt="Jonathan Mwebaze"
                  className="w-24 h-24 rounded-2xl object-cover"
                />

                <div>
                  <h3 className="text-2xl font-semibold text-[#1b140f]">Jonathan Mwebaze</h3>
                  <p className="text-sm text-[#8b5e3c]">Founder • Farmer • Software Engineer</p>
                </div>
              </div>

              <p className="mt-6 text-[15px] leading-7 text-[#4f4034]">
                Fundo is not an abstract idea. It comes from direct experience with how agriculture
                actually works, the inefficiencies, the missed opportunities, and the lack of
                accessible systems.
              </p>

              <p className="mt-4 text-[15px] leading-7 text-[#4f4034]">
                The goal is simple! Build the infrastructure layer that agriculture has never had.
                One that connects farmers, capital, and markets into a single, intelligent system.
              </p>
            </div>
          </div>

          {/* RIGHT — TEAM SYSTEM */}
          <div className="grid gap-6">
            {[
              {
                title: 'Engineering',
                desc: 'Building scalable infrastructure for agriculture systems.',
              },
              {
                title: 'Agronomy',
                desc: 'Grounding decisions in real farming knowledge.',
              },
              {
                title: 'Markets',
                desc: 'Connecting production to transparent global demand.',
              },
            ].map((item, i) => (
              <div
                key={i}
                className="rounded-[1.5rem] border border-[#1b140f]/10 bg-white/60 backdrop-blur-md p-6 transition hover:scale-[1.02]"
              >
                <h4 className="font-semibold text-[#1b140f]">{item.title}</h4>
                <p className="mt-2 text-sm text-[#4f4034] leading-6">{item.desc}</p>
              </div>
            ))}

            {/* FUTURE TEAM SIGNAL */}
            <div className="rounded-[1.5rem] border border-dashed border-[#1b140f]/20 p-6 text-sm text-[#8b5e3c]">
              Fundo is actively expanding across engineering, agriculture, and finance.
            </div>
          </div>
        </div>
      </section>

      {/* CLOSING */}
      <section className="mt-32 text-center">
        <h2 className="font-serif text-4xl text-[#1b140f]">
          Agriculture is evolving. Fundo is building what comes next.
        </h2>

        <p className="mt-6 text-[#4f4034]">
          A more connected, inclusive, and intelligent agricultural system — designed for everyone.
        </p>
      </section>
    </MainLayout>
  );
};
